import { apply, onceToday } from '../core/effects';
import { m, type Messages } from '../i18n';
import { sfx } from '../audio/sfx';
import { h } from '../ui/dom';
import { openEnglish } from '../ui/english';
import { closePhone, openPhone, popView, pushView, registerTab, render } from './phone';
import { dadPreview, dadRoom } from './dadChat';
import { bindMomRender, momChoices, momPreview, momThread, momTyping, momUnanswered } from './momTexts';

type ChatId = keyof Messages['phone']['chats'];
type Card = Messages['phone']['card'][number];

interface Msg {
  side: 'me' | 'them' | 'sep';
  from?: string;
  text?: string;
  card?: Card;
  img?: string;
}

/** Messages sent during this session, appended after each chat's opening lines. */
const extra: Record<ChatId, Msg[]> = { family: [], friends: [], mom: [], emma: [] };
let typing: ChatId | null = null;

const AVATAR: Record<ChatId, [string, string]> = {
  family: ['🏠', '#e67e22'],
  friends: ['👥', '#27ae60'],
  mom: ['👩', '#e74c3c'],
  emma: ['E', '#3498db'],
};

function opening(id: ChatId): Msg[] {
  // Mom's room shows her morning texts of the last two weeks instead of fixed lines.
  if (id === 'mom') return momThread();
  const c = m().phone.chats[id];
  return c.msgs.map((x) => (x.text === '@card' ? { side: 'them', from: x.from, card: m().phone.card[0] } : { side: 'them', from: x.from || undefined, text: x.text }));
}

function post(id: ChatId, msg: Msg, delay = 0): void {
  if (!delay) {
    extra[id].push(msg);
    render();
    return;
  }
  typing = id;
  render();
  window.setTimeout(() => {
    typing = null;
    extra[id].push(msg);
    if (msg.side === 'them') sfx('pop');
    render();
  }, delay);
}

function card(c: Card): HTMLElement {
  return h('div', { class: 'elder-card' }, h('div', { class: 'sun' }, c.top), h('div', { class: 'big' }, c.big), h('div', { class: 'sub' }, c.sub.replace('\n', ' · ')));
}

const SEP_STYLE = 'align-self:center;font-size:10px;font-weight:700;color:#b9a896;background:rgba(255,255,255,0.07);padding:2px 10px;border-radius:10px;margin:6px 0 2px';

function bubble(msg: Msg): HTMLElement {
  if (msg.side === 'sep') return h('div', { class: 'chat-sep', style: SEP_STYLE }, msg.text);
  return h(
    'div',
    { class: `bubble ${msg.side}${msg.img ? ' photo' : ''}` },
    msg.from ? h('div', { class: 'from' }, msg.from) : null,
    msg.img ? h('img', { src: msg.img, alt: '', class: 'chat-img' }) : msg.card ? card(msg.card) : msg.text,
  );
}

function list() {
  const t = m().phone;
  const ids: ChatId[] = ['family', 'friends', 'mom', 'emma'];
  return {
    title: t.chatsTitle,
    body: h(
      'div',
      { class: 'ph-list' },
      h(
        'button',
        { class: 'ph-item', onclick: () => (sfx('click'), pushView(dadRoom)) },
        h('span', { class: 'av', style: 'background:#3a8fd9' }, '👨'),
        h('span', { class: 'info' }, h('b', null, m().dadChat.name), h('small', null, dadPreview())),
        h('span', { class: 'tm' }, '07:30'),
        h('span', { class: 'dot' }),
      ),
      ids.map((id) => {
        const c = t.chats[id];
        const [av, col] = AVATAR[id];
        const last = extra[id][extra[id].length - 1];
        const preview = last?.text ?? (id === 'mom' ? momPreview() : c.preview);
        return h(
          'button',
          { class: 'ph-item', onclick: () => (sfx('click'), pushView(() => room(id))) },
          h('span', { class: 'av', style: `background:${col}` }, av),
          h('span', { class: 'info' }, h('b', null, c.name), h('small', null, preview)),
          h('span', { class: 'tm' }, last ? t.now : id === 'emma' ? t.yesterday : '08:0' + ids.indexOf(id)),
          id === 'mom' && momUnanswered() ? h('span', { class: 'dot' }) : null,
        );
      }),
    ),
  };
}

function choices(id: ChatId): { text: string; run: () => void }[] {
  const t = m().phone.chats;
  const me = (text: string) => post(id, { side: 'me', text });
  const them = (from: string, text: string, delay: number) => post(id, { side: 'them', from, text }, delay);
  switch (id) {
    case 'family':
      return [
        {
          text: t.family.choices[0],
          run: () => {
            me(t.family.choices[0].replace(/^[^:：]+[:：]\s*/, ''));
            if (onceToday('chat.family1')) apply({ mood: 3 });
            t.family.r1.forEach((r, i) => them(r.from, r.text, 700 + i * 800));
          },
        },
        { text: t.family.choices[1], run: () => sendCard(id) },
        {
          text: t.family.choices[2],
          run: () => {
            me(t.family.choices[2].replace(/^[^:：]+[:：]\s*/, ''));
            if (onceToday('chat.family3')) apply({ mood: 4, love: { sister: 5 } });
            t.family.r3.forEach((r, i) => them(r.from, r.text, 700 + i * 800));
          },
        },
      ];
    case 'friends':
      return t.friends.choices.map((c, i) => ({
        text: c,
        run: () => {
          me(c.replace(/^[^:：]+[:：]\s*/, ''));
          if (i === 0) {
            if (onceToday('chat.friends1')) apply({ stars: 3 });
            t.friends.r1.forEach((r, k) => them(r.from, r.text, 700 + k * 800));
          } else {
            const r = i === 1 ? t.friends.r2 : t.friends.r3;
            them(r.from, r.text, 700);
          }
        },
      }));
    case 'mom': {
      // Today's morning text first; the everyday replies once that talk is over.
      const today = momChoices();
      if (today.length || momTyping()) return today;
      return [
        {
          text: t.mom.choices[0],
          run: () => {
            me(t.mom.choices[0].replace(/^[^:：]+[:：]\s*/, ''));
            if (onceToday('chat.mom')) apply({ love: { mom: 5 } }, { float: false });
            them(m().npc.mom, m().phone.chats.mom.r1, 700);
          },
        },
        { text: t.mom.choices[1], run: () => sendCard(id) },
      ];
    }
    case 'emma':
      return [
        {
          text: t.emma.choices[0],
          run: () => {
            me(t.emma.r1me);
            them('Emma', m().phone.chats.emma.r1, 700);
            window.setTimeout(() => {
              closePhone();
              openEnglish();
            }, 1600);
          },
        },
        {
          text: t.emma.choices[1],
          run: () => {
            me(t.emma.r2me);
            them('Emma', m().phone.chats.emma.r2, 700);
          },
        },
      ];
  }
}

function sendCard(id: ChatId): void {
  post(id, { side: 'me', card: m().phone.card[1] });
  if (onceToday(`chat.card.${id}`)) apply({ mood: 3 });
  const r = m().phone.chats.family.cardReply;
  post(id, { side: 'them', from: id === 'mom' ? m().npc.mom : r.from, text: r.text }, 800);
}

function autoReply(id: ChatId, text: string): void {
  const t = m().phone;
  const lower = text.toLowerCase();
  let reply = t.defaultReply;
  let from: string | undefined;
  if (id === 'mom') {
    const c = t.chats.mom;
    from = m().npc.mom;
    if (c.kwMorning.some((k) => lower.includes(k.toLowerCase()))) reply = c.replyMorning;
    else if (c.kwLove.some((k) => lower.includes(k.toLowerCase()))) reply = c.replyLove;
    else reply = c.replyOther;
  } else if (id === 'family') {
    const r = t.chats.family.replies;
    reply = r[Math.floor(Math.random() * r.length)];
    from = m().npc.mom;
  } else if (id === 'emma') {
    const r = t.chats.emma.replies;
    reply = r[Math.floor(Math.random() * r.length)];
    from = 'Emma';
  } else from = t.chats.friends.msgs[0].from;
  post(id, { side: 'them', from, text: reply }, 800);
}

function room(id: ChatId) {
  const t = m().phone;
  const input = h('input', { type: 'text', placeholder: t.placeholder, maxlength: 200, autocomplete: 'off', enterkeyhint: 'send', 'aria-label': t.placeholder });
  const send = () => {
    const v = input.value.trim();
    if (!v) return;
    input.value = '';
    sfx('pop');
    post(id, { side: 'me', text: v });
    autoReply(id, v);
  };
  input.addEventListener('keydown', (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') send();
  });
  let stickersOpen = false;
  const stickerPanel = h(
    'div',
    { class: 'sticker-panel', hidden: true },
    t.stickerLines.map((line) =>
      h(
        'button',
        {
          onclick: () => {
            sfx('pop');
            post(id, { side: 'me', text: line });
          },
        },
        Array.from(line)[0],
      ),
    ),
  );
  const bar = h(
    'div',
    { class: 'ph-bottom' },
    stickerPanel,
    h(
      'div',
      { class: 'ph-input' },
      h(
        'button',
        {
          class: 'round ghost',
          'aria-label': t.stickers,
          onclick: () => {
            stickersOpen = !stickersOpen;
            stickerPanel.hidden = !stickersOpen;
          },
        },
        '😀',
      ),
      input,
      h('button', { class: 'round green', 'aria-label': t.send, onclick: send }, '➤'),
    ),
  );
  return {
    title: t.chats[id].name,
    back: popView,
    body: [
      h(
        'div',
        { class: 'room-msgs' },
        [...opening(id), ...extra[id]].map(bubble),
        typing === id || (id === 'mom' && momTyping()) ? h('div', { class: 'bubble them typing' }, h('i'), h('i'), h('i')) : null,
      ),
      h(
        'div',
        { class: 'room-choices' },
        choices(id).map((c) => h('button', { onclick: () => (sfx('click'), c.run()) }, c.text)),
      ),
    ],
    bar,
  };
}

export function initChats(): void {
  registerTab('chats', list);
  bindMomRender(render);
}

/** Open the phone straight on Mom's thread (pause menu "📩 Mom's messages"). */
export function openMomThread(): void {
  openPhone('chats');
  pushView(() => room('mom'));
}
